import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  Calendar as CalIcon,
  Edit,
  Trash2,
} from "lucide-react";
import { PageTitle } from "@/components/common/Crud";
import { DatePicker } from "@/components/common/DatePicker";
import { MultiCombobox } from "@/components/common/Combobox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EVENT_COLORS = [
  { value: "#4F46E5", label: "Indigo" },
  { value: "#16A34A", label: "Green" },
  { value: "#DC2626", label: "Red" },
  { value: "#D97706", label: "Amber" },
  { value: "#0891B2", label: "Cyan" },
  { value: "#7C3AED", label: "Violet" },
  { value: "#DB2777", label: "Pink" },
  { value: "#475569", label: "Slate" },
];

export default function CalendarPage() {
  const { branchId } = useAuth();
  const [today] = useState(new Date());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [form, setForm] = useState({});

  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const startDow = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();

  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const load = async () => {
    const from = fmt(new Date(year, month - 1, 25));
    const to = fmt(new Date(year, month + 1, 7));
    const { data } = await api.get("/calendar-events", {
      params: { branch_id: branchId, from_date: from, to_date: to },
    });
    setEvents(data.items || []);
  };
  useEffect(() => {
    load();
  }, [branchId, year, month]);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/staff", {
        params: { branch_id: branchId, limit: 500 },
      });
      setStaff(data.items || []);
    })();
  }, [branchId]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const s = new Date(e.start_date);
      const en = new Date(e.end_date || e.start_date);
      for (let d = new Date(s); d <= en; d.setDate(d.getDate() + 1)) {
        const k = fmt(d);
        (map[k] = map[k] || []).push(e);
      }
    }
    return map;
  }, [events]);

  const openCreate = (date) => {
    setEditing(null);
    setForm({
      title: "",
      description: "",
      start_date: date || fmt(new Date()),
      end_date: date || fmt(new Date()),
      color: "#4F46E5",
      staff_ids: [],
      multi_day: false,
      branch_id: branchId,
    });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...e,
      staff_ids: e.staff_ids || [],
      multi_day: e.start_date !== e.end_date,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.start_date)
      return toast.error("Title and Start Date required");
    const payload = { ...form };
    if (!payload.multi_day) payload.end_date = payload.start_date;
    delete payload.multi_day;
    if (editing) await api.put(`/calendar-events/${editing.id}`, payload);
    else await api.post(`/calendar-events`, payload);
    toast.success(editing ? "Updated" : "Event created");
    setOpen(false);
    load();
  };

  const del = async () => {
    if (!deleteRow?.id) return;
    await api.delete(`/calendar-events/${deleteRow.id}`);
    toast.success("Deleted");
    setDeleteRow(null);
    load();
  };

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else setMonth(month + 1);
  };

  const monthName = new Date(year, month).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div data-testid="calendar-page">
      <PageTitle
        title="Calendar"
        subtitle="School events, exams, PTM, holidays · tag staff for visibility"
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={prevMonth}
              className="h-8"
              data-testid="cal-prev"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <div
              className="px-3 h-8 flex items-center font-bold erp-text-primary font-serif-display"
              data-testid="cal-month"
            >
              {monthName}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={nextMonth}
              className="h-8"
              data-testid="cal-next"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              onClick={() => openCreate(null)}
              className="h-8 erp-primary"
              data-testid="cal-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Event
            </Button>
          </>
        }
      />

      <div className="border border-slate-200 bg-white rounded-md overflow-hidden shadow-sm">
        <div className="grid grid-cols-7 bg-slate-50 border-b text-[10px] font-bold uppercase tracking-wider text-slate-600">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
            <div
              key={d}
              className={`p-2 text-center ${i === 0 ? "text-rose-600" : ""}`}
            >
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const dateStr = d ? fmt(new Date(year, month, d)) : null;
            const dayEvents = dateStr ? eventsByDay[dateStr] || [] : [];
            const isToday = dateStr === fmt(today);
            const dow = i % 7;
            return (
              <div
                key={i}
                className={`min-h-[90px] border-r border-b border-slate-100 p-1.5 ${d ? "hover:bg-slate-50 cursor-pointer" : "bg-slate-50/40"}`}
                onClick={() => d && openCreate(dateStr)}
                data-testid={d ? `day-${d}` : ""}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-xs font-bold ${isToday ? "bg-[var(--erp-primary)] text-white w-5 h-5 rounded-full flex items-center justify-center" : dow === 0 ? "text-rose-600" : "text-slate-700"}`}
                      >
                        {d}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            openEdit(e);
                          }}
                          className="w-full text-left text-[10px] px-1.5 py-0.5 rounded text-white truncate font-semibold"
                          style={{ background: e.color || "#4F46E5" }}
                          data-testid={`event-${e.id}`}
                        >
                          {e.title}
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[9px] text-slate-500 font-semibold">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif-display erp-text-primary">
              {editing ? "Edit Event" : "New Event"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Title *</Label>
              <Input
                value={form.title || ""}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="h-9"
                data-testid="ev-title"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Description</Label>
              <textarea
                rows={2}
                value={form.description || ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="w-full rounded-md border border-slate-300 text-sm px-2 py-1.5"
                data-testid="ev-desc"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="multi"
                checked={!!form.multi_day}
                onChange={(e) =>
                  setForm({ ...form, multi_day: e.target.checked })
                }
                data-testid="ev-multi"
              />
              <label htmlFor="multi" className="text-xs font-semibold">
                Multi-day event
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">
                  {form.multi_day ? "Start Date" : "Event Date"} *
                </Label>
                <DatePicker
                  value={form.start_date || ""}
                  onChange={(v) => setForm({ ...form, start_date: v })}
                  className="h-9"
                  testId="ev-start"
                />
              </div>
              {form.multi_day && (
                <div>
                  <Label className="text-xs font-semibold">End Date *</Label>
                  <DatePicker
                    value={form.end_date || ""}
                    onChange={(v) => setForm({ ...form, end_date: v })}
                    className="h-9"
                    testId="ev-end"
                  />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1.5 block">
                Color
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {EVENT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: c.value })}
                    className={`w-7 h-7 rounded-full border-2 transition ${form.color === c.value ? "border-slate-900 scale-110" : "border-white"} shadow`}
                    style={{ background: c.value }}
                    title={c.label}
                    data-testid={`ev-color-${c.label.toLowerCase()}`}
                  />
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold">
                Tag Staff (optional)
              </Label>
              <MultiCombobox
                value={form.staff_ids || []}
                onChange={(v) => setForm({ ...form, staff_ids: v })}
                options={staff.map((s) => ({
                  value: s.id,
                  label: `${s.first_name} ${s.last_name || ""} · ${s.role}`,
                }))}
                placeholder="All staff"
                testid="ev-staff"
              />
            </div>
          </div>
          <DialogFooter>
            {editing && (
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteRow(editing);
                  setOpen(false);
                }}
                className="text-rose-600"
                data-testid="ev-delete"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="erp-primary"
              data-testid="ev-save"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteRow}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setDeleteRow(null);
        }}
      >
        <AlertDialogContent className="max-w-md border-slate-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-slate-900">
              Delete "{deleteRow?.title}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-6 text-slate-600">
              This will permanently remove the selected calendar event.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={del}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* Dashboard widget — upcoming events list */
export function UpcomingEventsWidget() {
  const { branchId } = useAuth();
  const [events, setEvents] = useState([]);
  useEffect(() => {
    (async () => {
      const today = new Date().toISOString().slice(0, 10);
      const to = new Date();
      to.setDate(to.getDate() + 30);
      const { data } = await api.get("/calendar-events", {
        params: {
          branch_id: branchId,
          from_date: today,
          to_date: to.toISOString().slice(0, 10),
        },
      });
      setEvents((data.items || []).slice(0, 6));
    })();
  }, [branchId]);
  return (
    <div className="border border-slate-200 bg-white rounded-xl overflow-hidden shadow-sm">
      <div className="px-3.5 py-2.5 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
            <CalIcon className="w-3.5 h-3.5" />
          </div>
          <span className="font-semibold text-sm">Upcoming Events</span>
        </div>
        <a
          href="/calendar"
          className="text-[11px] erp-text-primary font-semibold hover:underline"
        >
          View calendar →
        </a>
      </div>
      <div className="divide-y divide-slate-100">
        {events.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-slate-400">
            No upcoming events
          </div>
        )}
        {events.map((e) => (
          <div key={e.id} className="px-3.5 py-2 flex items-center gap-2">
            <div
              className="w-1 h-8 rounded"
              style={{ background: e.color || "#4F46E5" }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-slate-800 truncate">
                {e.title}
              </div>
              <div className="text-[10px] text-slate-500">
                {e.start_date}
                {e.end_date && e.end_date !== e.start_date
                  ? ` → ${e.end_date}`
                  : ""}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
